import React, { useEffect, useCallback, useRef } from 'react';
import LoadingButton from './LoadingButton';

export default function ConfirmModal({
  isOpen,
  title = 'Are you sure?',
  message = 'Do you really want to proceed? This action cannot be undone.',
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  loadingText = 'Deleting...',
  variant = 'danger',
}) {
  const modalRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const previousFocusRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && !isLoading) {
      e.preventDefault();
      onCancel();
      return;
    }
    if (e.key !== 'Tab' || !modalRef.current) return;

    const focusable = modalRef.current.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, [onCancel, isLoading]);

  useEffect(() => {
    if (!isOpen) return undefined;
    previousFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      if (cancelBtnRef.current) cancelBtnRef.current.focus();
    }, 0);

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="confirm-modal-backdrop"
      onClick={isLoading ? undefined : onCancel}
    >
      <div
        ref={modalRef}
        className="confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="confirm-modal-close"
          onClick={onCancel}
          aria-label="Close"
          disabled={isLoading}
        >
          ×
        </button>

        <div
          className={`confirm-modal-icon confirm-modal-icon-${variant}`}
          aria-hidden="true"
        >
          {variant === 'danger' ? (
            <>
              <span className="confirm-modal-icon-line confirm-modal-icon-line-1" />
              <span className="confirm-modal-icon-line confirm-modal-icon-line-2" />
            </>
          ) : (
            <span className="confirm-modal-icon-glyph">
              {variant === 'warning' ? '!' : 'i'}
            </span>
          )}
        </div>

        <h2 id="confirm-modal-title" className="confirm-modal-title">{title}</h2>
        <p className="confirm-modal-description">{message}</p>

        <div className="confirm-modal-actions">
          <button
            ref={cancelBtnRef}
            type="button"
            className="confirm-modal-btn cancel"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelLabel}
          </button>
          <LoadingButton
            type="button"
            className={`confirm-modal-btn ${variant === 'danger' ? 'delete' : variant}`}
            onClick={onConfirm}
            disabled={isLoading}
            loading={isLoading}
            loadingText={loadingText}
          >
            {confirmLabel}
          </LoadingButton>
        </div>
      </div>
      <style>{css}</style>
    </div>
  );
}

const css = `
.confirm-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 2000;
  animation: fadeInUp 0.18s ease-out;
}

.confirm-modal {
  position: relative;
  width: 100%;
  max-width: 420px;
  background: #FFFFFF;
  border-radius: 18px;
  padding: 24px 20px 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  text-align: center;
  animation: scaleIn 0.2s ease-out;
}

.confirm-modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: #94A3B8;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-modal-close:hover {
  color: #475569;
  background: #F8FAFC;
}

.confirm-modal-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 2px solid #EF4444;
  margin: 0 auto 16px;
  position: relative;
}

.confirm-modal-icon-line {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 36px;
  height: 3px;
  background: #EF4444;
  border-radius: 999px;
  transform-origin: center;
}

.confirm-modal-icon-line-1 {
  transform: translate(-50%, -50%) rotate(45deg);
}

.confirm-modal-icon-line-2 {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.confirm-modal-icon-info {
  border-color: #2563EB;
}

.confirm-modal-icon-info .confirm-modal-icon-glyph {
  color: #2563EB;
}

.confirm-modal-icon-warning {
  border-color: #D97706;
}

.confirm-modal-icon-warning .confirm-modal-icon-glyph {
  color: #D97706;
}

.confirm-modal-icon-glyph {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  font-style: italic;
  font-family: Georgia, serif;
}

.confirm-modal-title {
  margin: 0 0 8px;
  font-size: 20px;
  color: #0F172A;
  font-weight: 600;
}

.confirm-modal-description {
  margin: 0 auto;
  max-width: 300px;
  font-size: 14px;
  line-height: 1.6;
  color: #64748B;
}

.confirm-modal-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
}

.confirm-modal-btn {
  min-width: 116px;
  padding: 11px 18px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.confirm-modal-btn:hover {
  transform: translateY(-1px);
}

.confirm-modal-btn.cancel {
  background: #D1D5DB;
  color: #374151;
}

.confirm-modal-btn.cancel:hover {
  background: #BFC6D1;
}

.confirm-modal-btn.delete {
  background: #EF4444;
  color: #FFFFFF;
  box-shadow: 0 8px 18px rgba(239, 68, 68, 0.24);
}

.confirm-modal-btn.delete:hover {
  background: #DC2626;
  box-shadow: 0 10px 20px rgba(239, 68, 68, 0.30);
}

.confirm-modal-btn.info {
  background: #2563EB;
  color: #FFFFFF;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.24);
}

.confirm-modal-btn.info:hover {
  background: #1D4ED8;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.30);
}

.confirm-modal-btn.warning {
  background: #D97706;
  color: #FFFFFF;
  box-shadow: 0 8px 18px rgba(217, 119, 6, 0.24);
}

.confirm-modal-btn.warning:hover {
  background: #B45309;
  box-shadow: 0 10px 20px rgba(217, 119, 6, 0.30);
}

.confirm-modal-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}
`;
